// filepath: /Users/ephraim/yesodcopilot/client/src/pages/group/EventDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faMapMarkerAlt, 
  faBookmark as faBookmarkSolid,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { faBookmark as faBookmarkRegular } from '@fortawesome/free-regular-svg-icons';
import { Share2 } from 'lucide-react';
import { useGroup } from '../../context/GroupContext';
import { useAuth } from '../../context/AuthContext';
import groupService from '../../api/groupService';
import PaymentLinksModal from '../../components/modals/PaymentLinksModal';
// import SponsorModal from '../../components/modals/SponsorModal'; // Commented out old modal
import EventRsvpModal from '../../components/modals/EventRsvpModal';
import AppDownloadModal from '../../components/modals/AppDownloadModal';
import NumberPicker from '../../components/common/NumberPicker';
import { renderLocationWithLinks } from '../../utils/linkUtils';
import { createShareLinks } from '../../config';
import '../../styles/group/EventDetails.css';

const EventDetails = () => {
  // URL params
  const { id: groupId, eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { adminGroup, userGroups } = useGroup();
  const { isAuthenticated, currentUser } = useAuth();
  
  // Determine if this is being viewed from public community page
  // Always show public view when accessed via /event/ (singular) route
  const isPublicView = location.pathname.includes('/event/');
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState(null);
  const [isRsvpLoading, setIsRsvpLoading] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [attendeesCount, setAttendeesCount] = useState(0);
  const [attendeeCount, setAttendeeCount] = useState(1); // Number of people user is bringing
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showPublicRegisterModal, setShowPublicRegisterModal] = useState(false);
  
  // Handle image loading errors
  const [headerImageError, setHeaderImageError] = useState(false);

  // Direct fix for Supabase URLs with special characters
  const processImageUrl = (url) => {
    if (!url) return null;
    
    try {
      // Remove excessive logging
      // console.log('EventDetails - Original image URL:', url);
      
      // First try to create a valid URL object to see if it's parseable
      try {
        new URL(url);
      } catch (e) {
        console.error('Invalid URL format:', url);
        return null;
      }
      
      // Extract the base domain and path
      const urlObj = new URL(url);
      const baseDomain = `${urlObj.protocol}//${urlObj.host}`;
      const pathParts = urlObj.pathname.split('/');
      
      // Process each part of the path to properly encode
      const processedPathParts = pathParts.map((part, index) => {
        // Don't encode the first empty part (from the leading slash)
        if (index === 0 && part === '') return '';
        
        // For the filename (last part), ensure it's properly encoded
        if (index === pathParts.length - 1) {
          try {
            // First try to decode it in case it's already encoded but with issues
            const decodedPart = decodeURIComponent(part);
            // Then properly encode it
            return encodeURIComponent(decodedPart);
          } catch (e) {
            // If decoding fails, just encode as is
            return encodeURIComponent(part);
          }
        }
        
        // For path segments, just ensure they're properly encoded
        return encodeURIComponent(part);
      });
      
      // Build the URL back
      const processedPath = processedPathParts.join('/');
      let processedUrl = `${baseDomain}${processedPath}`;
      
      // Add query parameters if they exist
      if (urlObj.search) {
        processedUrl += urlObj.search;
      }
      
      // Remove excessive logging
      // console.log('EventDetails - Processed image URL:', processedUrl);
      
      // Add a cache buster to prevent browser caching issues
      const separator = processedUrl.includes('?') ? '&' : '?';
      return `${processedUrl}${separator}t=${Date.now()}`;
    } catch (error) {
      console.error('Error processing image URL:', error);
      return url;
    }
  };

  // Handle share event
  const handleShareEvent = async (e) => {
    e.stopPropagation();
    const shareUrl = createShareLinks.event(eventId);
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      showCopyToast();
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Show copy toast notification
  const showCopyToast = () => {
    const toast = document.createElement('div');
    toast.className = 'copy-toast';
    toast.textContent = 'Link copied to clipboard!';
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.remove();
    }, 3000);
  };

  // Fetch event details, RSVPs, and user's RSVP status
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        
        // Fetch event details (this now works for both authenticated and unauthenticated users)
        const response = await groupService.getEvent(groupId, eventId);
        setEvent(response.data);
        
        // Set initial attendees count from the event data to match EventCard display
        if (response.data && response.data.attendees_count !== undefined) {
          setAttendeesCount(response.data.attendees_count);
        }
        
        // Only fetch RSVP data if user is authenticated and NOT in public view
        if (currentUser && !isPublicView) {
          // Fetch RSVP status for the current user
          try {
            const rsvpResponse = await groupService.getUserEventRsvp(groupId, eventId);
            console.log('User RSVP response:', rsvpResponse);
            
            if (rsvpResponse.success && rsvpResponse.data) {
              setRsvpStatus(rsvpResponse.data.status);
              
              // Set the attendee count from the user's saved RSVP
              if (rsvpResponse.data.attendee_count) {
                setAttendeeCount(rsvpResponse.data.attendee_count);
                console.log(`Loaded user's saved attendee count: ${rsvpResponse.data.attendee_count}`);
              }
            }
          } catch (rsvpError) {
            console.error('Error fetching RSVP status:', rsvpError);
          }
          
          // Fetch all RSVPs for the event to display attendee profiles
          refreshAttendees();
        } else {
          // Clear RSVP data when not authenticated
          setRsvpStatus(null);
          setAttendees([]);
          setAttendeeCount(1);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching event details:', error);
        setError('Failed to load event details. Please try again later.');
        setLoading(false);
      }
    };

    fetchEventData();
  }, [groupId, eventId, currentUser]);

  // Show app download modal on page load (with a small delay to ensure page renders first)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Check if user hasn't dismissed the modal recently (within 24 hours)
      const lastDismissed = localStorage.getItem('appDownloadModalDismissed');
      const now = Date.now();
      const oneDayInMs = 24 * 60 * 60 * 1000;
      
      if (!lastDismissed || (now - parseInt(lastDismissed)) > oneDayInMs) {
        setShowDownloadModal(true);
      }
    }, 500); // Show after 500ms to let the page render

    return () => clearTimeout(timer);
  }, []);

  // Handle RSVP toggle
  const handleRsvp = async () => {
    try {
      if (rsvpStatus) {
        // Already RSVP'd, so remove RSVP
        setIsRsvpLoading(true);
        
        // Optimistically update the UI first
        const oldAttendeesCount = attendeesCount;
        const oldAttendeeCount = attendeeCount;
        const oldRsvpStatus = rsvpStatus;
        
        setRsvpStatus(null);
        setAttendeesCount(Math.max(0, attendeesCount - oldAttendeeCount));
        
        try {
          await groupService.deleteEventRsvp(groupId, eventId);
          console.log('RSVP Debug - Successfully removed RSVP');
        } catch (error) {
          // Revert optimistic update on error
          setRsvpStatus(oldRsvpStatus);
          setAttendeesCount(oldAttendeesCount);
          throw error;
        }
      } else {
        // Open RSVP modal for new RSVP
        setShowRsvpModal(true);
        return; // Exit early to avoid setting loading state
      }
    } catch (error) {
      console.error('Error handling RSVP:', error);
      if (window.showToast) {
        window.showToast('Error updating RSVP status', 'error');
      }
    } finally {
      setIsRsvpLoading(false);
    }
  };

  const handleRsvpSubmit = async (rsvpData) => {
    try {
      setIsRsvpLoading(true);
      
      console.log('RSVP Debug - Submitting RSVP data:', rsvpData);
      
      // Optimistically update the UI first
      const oldAttendeesCount = attendeesCount;
      const oldRsvpStatus = rsvpStatus;
      
      setRsvpStatus('going');
      setAttendeesCount(attendeesCount + rsvpData.attendee_count);
      setAttendeeCount(rsvpData.attendee_count); // Update the counter to match submitted count
      
      try {
        const response = await groupService.rsvpEvent(groupId, eventId, rsvpData);
        console.log('RSVP Debug - Server response:', response);
        
        if (!response.success) {
          // Revert optimistic update if server response indicates failure
          setRsvpStatus(oldRsvpStatus);
          setAttendeesCount(oldAttendeesCount);
          throw new Error('Server returned unsuccessful response');
        }
        
        console.log('RSVP Debug - Success, keeping optimistic updates');
        setShowRsvpModal(false); // Close modal on success
        
        if (window.showToast) {
          window.showToast('RSVP confirmed successfully!', 'success');
        }
        
        // Dispatch custom events to notify other components of RSVP count change
        const newCount = attendeesCount + rsvpData.attendee_count;
        const customEvent = new CustomEvent('rsvpCountUpdated', {
          detail: { 
            eventId: eventId, 
            newCount: newCount
          }
        });
        document.dispatchEvent(customEvent);
        
        // Also dispatch a refresh event for the UpcomingList
        const refreshEvent = new CustomEvent('upcomingListRefresh', {
          detail: { 
            groupId: groupId,
            eventId: eventId,
            newCount: newCount
          }
        });
        document.dispatchEvent(refreshEvent);
        
      } catch (error) {
        // Revert optimistic update on error
        setRsvpStatus(oldRsvpStatus);
        setAttendeesCount(oldAttendeesCount);
        throw error;
      }
      
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      if (window.showToast) {
        window.showToast('Error submitting RSVP', 'error');
      }
      setShowRsvpModal(false); // Close modal on error
    } finally {
      setIsRsvpLoading(false);
    }
  };

  // Refresh attendees list and count after RSVP changes
  const refreshAttendees = async () => {
    // Only fetch attendees if user is authenticated and NOT in public view
    if (!currentUser || isPublicView) {
      setAttendees([]);
      setAttendeesCount(0);
      return;
    }
    
    try {
      const allRsvpsResponse = await groupService.getEventRsvps(groupId, eventId);
      if (allRsvpsResponse && allRsvpsResponse.data) {
        const rsvpData = Array.isArray(allRsvpsResponse.data) 
          ? allRsvpsResponse.data.filter(rsvp => rsvp.status === 'going')
          : [];
        setAttendees(rsvpData.slice(0, 3));
        
        // Calculate total attendees by summing up attendee_count from each RSVP
        const totalAttendees = rsvpData.reduce((total, rsvp) => {
          console.log(`RSVP from ${rsvp.name || 'unknown'}, attendee_count:`, rsvp.attendee_count);
          // Use the attendee_count if available, default to 1 if not
          const count = rsvp.attendee_count !== undefined ? Number(rsvp.attendee_count) : 1;
          return total + count;
        }, 0);
        
        console.log(`Total calculated attendees: ${totalAttendees} from ${rsvpData.length} RSVPs`);
        setAttendeesCount(totalAttendees);
      } else {
        setAttendees([]);
        setAttendeesCount(0);
      }
    } catch (error) {
      console.error('Error refreshing attendees:', error);
      setAttendees([]);
      setAttendeesCount(0);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format time
  const formatTime = (dateString) => {
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleTimeString(undefined, options);
  };

  // Format recurring event display
  const formatRecurringDisplay = (isRecurring, recurrencePattern, recurrenceDay, dateString) => {
    if (!isRecurring || !recurrencePattern || recurrencePattern === 'none') {
      return null; // Use normal date display
    }
    
    const time = formatTime(dateString);
    
    if (recurrencePattern === 'daily') {
      return { text: `Every Day at ${time}`, showTime: false };
    } else if (recurrencePattern === 'weekly' && recurrenceDay) {
      const days = {
        'monday': 'Monday',
        'tuesday': 'Tuesday', 
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
      };
      const dayName = days[recurrenceDay.toLowerCase()] || recurrenceDay;
      return { text: `Every ${dayName} at ${time}`, showTime: false };
    }
    
    return null; // Fallback to normal date display
  };

  // Go back to group page or admin dashboard based on where user came from
  const handleBack = () => {
    // Check if user came from admin dashboard or community calendar
    if (location.state?.fromAdmin) {
      navigate(`/admin/dashboard/events`);
    } else if (location.state?.fromCalendar) {
      navigate(`/admin/dashboard/calendar`);
    } else {
      // Navigate back to Events tab in public community page
      navigate(`/group/${groupId}?tab=events`);
    }
  };

  // Open sponsor modal
  const openSponsorModal = () => {
    setShowSponsorModal(true);
  };

  // Close sponsor modal
  const handleCloseSponsorModal = () => {
    setShowSponsorModal(false);
  };

  // Utility function to get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Generate a consistent color from a string (name or id)
  const getAvatarColor = (str) => {
    if (!str) return '#5669FF'; // Default blue
    
    // Generate a hash from the string
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // List of vibrant colors that work well with white text
    const colors = [
      '#5669FF', // Primary blue
      '#4CAF50', // Green
      '#FF5722', // Deep Orange
      '#673AB7', // Deep Purple
      '#3F51B5', // Indigo
      '#009688', // Teal
      '#E91E63', // Pink
      '#F44336'  // Red
    ];
    
    // Use the hash to pick a color
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  return (
    <div className="event-details-page">
      {loading ? (
        <div className="loading-container">Loading event details...</div>
      ) : error ? (
        <div className="error-container">{error}</div>
      ) : event ? (
        <>
          {/* Event header with cover image */}
          <div className="event-details-header">
            {event.cover_image_url && !headerImageError ? (
              <img 
                className="event-header-img"
                src={processImageUrl(event.cover_image_url)}
                alt={event.title}
                onError={() => setHeaderImageError(true)}
              />
            ) : (
              <div 
                className="event-header-placeholder"
                style={{
                  backgroundColor: '#5669FF',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '4rem'
                }}
              >
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
            )}
            
            <div className="event-header-overlay"></div>
          </div>
          
          {/* RSVP section - only show for authenticated users NOT in public view */}
          {currentUser && !isPublicView && (
            <div className="event-attendees-container">
              <div className="attendees-section">
                <div className="attendees-avatars">
                  {/* Show 3 gray placeholder icons when no attendees */}
                  {attendees.length > 0 ? (
                    attendees.map((attendee, index) => (
                      <div 
                        key={index}
                        className="attendee-avatar"
                        style={{
                          backgroundImage: attendee.profile_image ? 
                            `url(${processImageUrl(attendee.profile_image)})` : 
                            'none',
                          backgroundColor: attendee.profile_image ? 'transparent' : getAvatarColor(attendee.name)
                        }}
                      >
                        {!attendee.profile_image && (
                          <span className="attendee-initials">
                            {getInitials(attendee.name)}
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    // Three gray placeholders when no attendees
                    <>
                      <div className="attendee-avatar empty-avatar"></div>
                      <div className="attendee-avatar empty-avatar"></div>
                      <div className="attendee-avatar empty-avatar"></div>
                    </>
                  )}
                </div>
                <div className="attendees-count">
                  {/* Always show the count, even when it's 0 */}
                  {`${attendeesCount} going`}
                </div>
              </div>
              
              {/* RSVP Controls */}
              <div className="rsvp-controls">
                <NumberPicker
                  value={attendeeCount}
                  onChange={setAttendeeCount}
                  min={1}
                  max={20}
                  size="medium"
                  disabled={isRsvpLoading}
                />
                <button 
                  className={`rsvp-btn ${rsvpStatus ? 'rsvp-confirmed' : ''}`}
                  onClick={handleRsvp}
                  disabled={isRsvpLoading}
                >
                  {isRsvpLoading ? '...' : (rsvpStatus ? 'Going' : 'RSVP')}
                </button>
              </div>
            </div>
          )}
          
          {/* Event details content */}
          <div className="event-details-content">
            <h1 className="event-title">{event.title}</h1>
            
            <div className="event-meta">
              {/* Date and time section */}
              <div className="event-date-time">
                <div className="event-icon-container">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
                <div className="event-info-container">
                  {(() => {
                    const recurringDisplay = formatRecurringDisplay(event.is_recurring, event.recurrence_pattern, event.recurrence_day, event.date);
                    if (recurringDisplay) {
                      return <div className="event-date">{recurringDisplay.text}</div>;
                    } else {
                      return (
                        <>
                          <div className="event-date">{formatDate(event.date)}</div>
                          <div className="event-time">{formatTime(event.date)}</div>
                        </>
                      );
                    }
                  })()}
                </div>
              </div>
              
              {/* Location section */}
              {event.location && (
                <div className="event-location">
                  <div className="event-icon-container">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </div>
                  <div className="event-location-text">
                    {renderLocationWithLinks(event.location)}
                  </div>
                </div>
              )}
            </div>
            
            {/* Description section */}
            <div className="event-description-section">
              <h2 className="event-description-title">About</h2>
              <div className="event-description">
                {event.description}
              </div>
            </div>
          </div>
          
          {/* Sponsor button - only show if sponsorship is allowed */}
          {event.allow_sponsorship && (
            <div className="sponsor-button-container">
              <button 
                className="sponsor-button-simple"
                onClick={openSponsorModal}
                aria-label="Sponsor this event"
              >
                SPONSOR THIS EVENT
              </button>
            </div>
          )}
          
          {/* Sponsor modal */}
          <PaymentLinksModal
            isOpen={showSponsorModal}
            onClose={handleCloseSponsorModal}
            groupId={groupId}
            eventId={eventId}
            eventTitle={event.title}
            isClass={false}
          />
          
          {/* RSVP modal */}
          <EventRsvpModal
            isOpen={showRsvpModal}
            onClose={() => setShowRsvpModal(false)}
            onSubmit={handleRsvpSubmit}
            event={event}
            attendeeCount={attendeeCount}
            isLoading={isRsvpLoading}
          />
          
          {/* App Download modal for general dismissible prompts */}
          <AppDownloadModal
            isOpen={showDownloadModal}
            onClose={() => {
              setShowDownloadModal(false);
              localStorage.setItem('appDownloadModalDismissed', Date.now().toString());
            }}
            contentType="event"
          />

          {/* App Download modal for public registration */}
          <AppDownloadModal
            isOpen={showPublicRegisterModal}
            onClose={() => setShowPublicRegisterModal(false)}
            title="Register via the Yesod App"
            message="To register for this event, please download the Yesod app."
          />

          {/* Fixed Register button at bottom for public view or non-authenticated users */}
          {(isPublicView || !currentUser) && (
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
              <div className="max-w-3xl mx-auto">
                <button
                  className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-lg font-bold rounded-xl transition-colors shadow-md"
                  onClick={() => setShowPublicRegisterModal(true)}
                >
                  Register
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="error-container">Event not found</div>
      )}
    </div>
  );
};

export default EventDetails;